import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SaveNoteArgs {
  companyId: string;
  note: string;
  userId: string | null;
  submitForReview?: boolean;
}

/**
 * Saves the rep's note onto `companies.notes`. When `submitForReview` is true,
 * also flags the row as pending for the batch `rep_note_validator.py` run:
 *   note_review_status = 'pending_review'
 *   note_submitted_for_review_at = now()
 *   note_submitted_for_review_by = <user id>
 *
 * A database trigger stamps notes_updated_at / notes_updated_by automatically,
 * but we pass them explicitly in case the trigger isn't present in every env.
 */
export function useSaveAccountNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, note, userId, submitForReview }: SaveNoteArgs) => {
      const nowIso = new Date().toISOString();
      const update: Record<string, unknown> = {
        notes: note,
        notes_updated_at: nowIso,
        notes_updated_by: userId,
      };
      if (submitForReview) {
        update.note_review_status = "pending_review";
        update.note_submitted_for_review_at = nowIso;
        update.note_submitted_for_review_by = userId;
      }
      const { error } = await (supabase as any)
        .from("companies")
        .update(update)
        .eq("id", companyId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["company-detail", vars.companyId] });
      queryClient.invalidateQueries({ queryKey: ["agent-verdict", vars.companyId] });
    },
  });
}
